/**
 * Das verbindliche Vokabular — von Eugen am 31.08.2026 entschieden (T005),
 * dokumentiert im Kapitel „Das verbindliche Vokabular" der CLAUDE.md.
 *
 * EINE Quelle für Freigabe-Status und Kanäle. Alles Neue nutzt diese Werte;
 * die alten Demo-Statuswelten (`contentStatusMeta` in
 * lib/format.ts) bleiben für die Mock-Ansichten stehen und ziehen hierauf
 * um, sobald ihre Bereiche echt werden (T050 Inhalte, T090 Redaktionsplan).
 */

/**
 * Der eine Freigabe-Statusfluss für Video, Foto und Social:
 * entwurf → beim_kunden → aenderung → in_arbeit → freigegeben →
 * veroeffentlicht. `in_arbeit` ist deckungsgleich mit dem Aufgaben-Status;
 * `veroeffentlicht` nutzt nur, was ein Live-Gehen kennt (Social).
 */
export const freigabeStatusWerte = [
	"entwurf",
	"beim_kunden",
	"aenderung",
	"in_arbeit",
	"freigegeben",
	"veroeffentlicht",
] as const;

export type FreigabeStatus = (typeof freigabeStatusWerte)[number];

export const freigabeStatusMeta: Record<
	FreigabeStatus,
	{ label: string; kurz: string; tone: "mute" | "accent" | "amber" | "blue" }
> = {
	entwurf: { label: "Entwurf", kurz: "Entwurf", tone: "mute" },
	beim_kunden: { label: "Beim Kunden", kurz: "Beim Kunden", tone: "blue" },
	aenderung: { label: "Änderung gewünscht", kurz: "Änderung", tone: "amber" },
	in_arbeit: { label: "Wird überarbeitet", kurz: "In Arbeit", tone: "blue" },
	freigegeben: { label: "Freigegeben", kurz: "Freigegeben", tone: "accent" },
	veroeffentlicht: { label: "Veröffentlicht", kurz: "Live", tone: "mute" },
};

/**
 * „Was heißt das?" (T126, Kopilot-Konzept): ein Satz Kundensprache je
 * Freigabe-Status — die eine Quelle für Hub und Freigaben-Seite.
 */
export const freigabeStatusErklaerung: Record<FreigabeStatus, string> = {
	entwurf: "Daran arbeiten wir noch intern — ihr seht es, sobald es zur Durchsicht bereit ist.",
	beim_kunden:
		"Liegt bei euch: Schaut rein, gebt frei oder kommentiert direkt an der Stelle, was anders sein soll.",
	aenderung: "Euer Feedback ist bei uns angekommen — wir haben es auf dem Tisch.",
	in_arbeit: "Wir arbeiten gerade euer Feedback ein; die neue Version erscheint hier von selbst.",
	freigegeben: "Von euch freigegeben — die finale Fassung, jederzeit hier zum Herunterladen.",
	veroeffentlicht: "Ist live — auf dem vereinbarten Kanal veröffentlicht.",
};

/**
 * Dasselbe für die Projektphasen aus Kundensicht (die Werte von
 * `ProjektPhase` im API-Vertrag).
 */
export const kundenPhasenErklaerung: Record<
	| "kickoff"
	| "gespraech-gefuehrt"
	| "drehvorbereitung"
	| "feedback"
	| "dreh"
	| "schnitt"
	| "freigabe"
	| "fertig",
	string
> = {
	kickoff:
		"Wir lernen euch kennen: Im Kick-off-Gespräch klären wir Ziel, Botschaft und Ablauf — ihr müsst nichts vorbereiten.",
	"gespraech-gefuehrt":
		"Das Gespräch ist geführt; aus euren Antworten entsteht gerade das Konzept mit Drehplan.",
	drehvorbereitung:
		"Die Drehvorbereitung liegt vor: Ablauf, Fragen und Checkliste — bitte kurz drüberschauen und kommentieren, was anders sein soll.",
	feedback: "Euer Feedback ist eingearbeitet — alles ist bereit für den Drehtag.",
	dreh: "Wir drehen: Kamera, Ton, Interviews — danach geht es direkt in den Schnitt.",
	schnitt:
		"Im Schnitt entstehen eure Videos. Sobald eine Fassung steht, bekommt ihr sie zur Durchsicht.",
	freigabe: "Eure Inhalte liegen zur Freigabe bei euch — schaut rein, kommentiert oder gebt frei.",
	fertig: "Alles freigegeben und ausgeliefert — eure Inhalte findet ihr dauerhaft hier im Bereich.",
};

/**
 * Der Kanal-Wortschatz für Benachrichtigungen und Erinnerungen — ersetzt
 * die früheren fünf Schreibweisen (diensthandy, SMS, freie Labels).
 */
export const zustellKanalWerte = ["app", "mail", "whatsapp", "push"] as const;

export type ZustellKanal = (typeof zustellKanalWerte)[number];

export const zustellKanalLabel: Record<ZustellKanal, string> = {
	app: "Mitgliederbereich",
	mail: "E-Mail",
	whatsapp: "WhatsApp",
	push: "Push",
};

/**
 * Die Konto-Rollen (T010) — Werte kommen typgleich aus dem Backend-Vertrag
 * (`KontoRolle` in `@olgen/api/auth`), hier stehen nur die UI-Labels.
 */
export const kontoRolleLabel: Record<import("@olgen/api/auth").KontoRolle, string> = {
	mitarbeiter: "Mitarbeiter",
	kunde: "Kunde",
};

/**
 * Die Team-Rollen (03.09.2026, LS-Muster „benannte Rolle mit Erklärsatz"):
 * Werte aus `@olgen/api/auth`, hier Label und der eine Satz, der die Rolle
 * auf der Team-Seite erklärt.
 */
export const teamRolleLabel: Record<import("@olgen/api/auth").TeamRolle, string> = {
	inhaber: "Inhaber",
	mitglied: "Mitarbeiter",
};

export const teamRolleBeschreibung: Record<import("@olgen/api/auth").TeamRolle, string> = {
	inhaber:
		"Verwaltet das Team (einladen, Rollen, sperren), schaltet Module bei Kunden frei und pflegt die Einstellungen — sonst alles wie ein Mitarbeiter.",
	mitglied:
		"Arbeitet in Projekten, Aufgaben, Kunden, Kalender und Redaktionsplan — alles außer Team-Verwaltung, Modulen und Einstellungen.",
};

/**
 * Die fünf Termin-Arten (T030) — EIN Termin-Modell für Kalender,
 * Projekt-Termine und später die Erinnerungs-Engine. Werte typgleich mit
 * dem API-Vertrag (`TerminArt` in `@/api`); `dot`/`chip` sind die
 * Kalender-Farben (Meeting blau und Abgabe amber wie in der alten Demo,
 * Drehtag gold).
 */
/** Die Arten, die das Team im Dialog anlegen kann. */
export const terminArtWaehlbar = [
	"kickoff",
	"dreh",
	"abgabe",
	"meeting",
	"veroeffentlichung",
] as const;
/** Alle Arten eines Termin-Eintrags — "belegt" kommt aus externen Kalendern (13.09.2026, nur lesend). */
export const terminArtWerte = [...terminArtWaehlbar, "belegt", "frist"] as const;

export const terminArtMeta: Record<
	import("@/api").TerminArt,
	{ label: string; dot: string; chip: string }
> = {
	// Farben nur aus den Signal-Tokens (DESIGN.md §2): Drehtag ist Gold (das
	// Wichtigste), Kick-off und Meeting ruhiges Blau, Abgabe die Warnfarbe.
	kickoff: { label: "Kick-off", dot: "bg-info", chip: "bg-info-soft text-info" },
	dreh: { label: "Drehtag", dot: "bg-accent", chip: "bg-accent-soft text-accent-deep" },
	abgabe: { label: "Abgabe", dot: "bg-warn", chip: "bg-warn-soft text-warn" },
	meeting: { label: "Meeting", dot: "bg-info", chip: "bg-info-soft text-info" },
	veroeffentlichung: {
		label: "Veröffentlichung",
		dot: "bg-ink-mute",
		chip: "bg-surface-2 text-ink-soft",
	},
	// Belegungen aus externen Kalendern (Eugens Google-Kalender): grau, ohne Projekt.
	belegt: { label: "Belegt (Kalender)", dot: "bg-line", chip: "bg-surface-2 text-ink-mute" },
	// Fristen (13.09.2026): fällige Aufgaben und ablaufende Angebote — die Warnfarbe, wie Abgaben.
	frist: { label: "Frist", dot: "bg-warn", chip: "bg-warn-soft text-warn" },
};
